import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

import { C, fz } from "./lib/theme.js";
import {
  computeMetrics,
  computeScore,
  defaultBenchmarks,
  defaultWeights,
  metricMeta,
  scoreColor,
  scoreBg,
  fmt,
  aggregateTTM,
  pickScoredMetrics,
  netLeakage,
} from "./lib/compute.js";
import { api } from "./lib/api.js";
import { fallbackProperties, fallbackPeriod } from "./data/fallbackProperties.js";
import LoginGate from "./components/LoginGate.jsx";
import ManageModal from "./components/ManageModal.jsx";
import TrendChart from "./components/TrendChart.jsx";
import CapExProgress from "./components/CapExProgress.jsx";

function IconUpload({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function IconGear({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ScoreBadge({ score, size = "md" }) {
  const sz =
    size === "lg"
      ? { w: fz(52), h: fz(52), fs: fz(22), r: fz(10) }
      : size === "sm"
      ? { w: fz(32), h: fz(32), fs: fz(13), r: fz(6) }
      : { w: fz(40), h: fz(40), fs: fz(17), r: fz(8) };
  return (
    <div
      style={{
        width: sz.w,
        height: sz.h,
        borderRadius: sz.r,
        background: scoreBg(score),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `2px solid ${scoreColor(score)}`,
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: sz.fs, fontWeight: 700, fontFamily: "'DM Mono',monospace", color: scoreColor(score) }}>
        {score}
      </span>
    </div>
  );
}

function ScoreBreakdown({ result, weights }) {
  const totalW = Object.values(weights).reduce((s, w) => s + w, 0) || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {Object.entries(result.components).map(([key, c]) => {
        const meta = metricMeta[key];
        const w = weights[key] || 0;
        return (
          <div
            key={key}
            style={{
              display: "grid",
              gridTemplateColumns: `${fz(110)}px 60px 60px 1fr 40px 30px`,
              alignItems: "center",
              gap: 6,
              padding: "3px 0",
              borderBottom: `1px solid ${C.borderLight}`,
            }}
          >
            <span style={{ fontSize: 11, color: C.sub }}>{meta.label}</span>
            <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: C.text, textAlign: "right" }}>
              {meta.fmt(c.actual)}
            </span>
            <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: C.dim, textAlign: "right" }}>
              / {meta.fmt(c.target)}
            </span>
            <div style={{ height: 6, background: C.borderLight, borderRadius: 3, overflow: "hidden" }}>
              <div
                style={{
                  width: `${c.score}%`,
                  height: "100%",
                  background: scoreColor(c.score),
                  borderRadius: 3,
                  transition: "width 0.4s",
                }}
              />
            </div>
            <span
              style={{
                fontSize: 11,
                fontFamily: "'DM Mono',monospace",
                color: scoreColor(c.score),
                textAlign: "right",
                fontWeight: 600,
              }}
            >
              {Math.round(c.score)}
            </span>
            <span style={{ fontSize: 10, color: C.dim, textAlign: "right" }}>
              {Math.round((w / totalW) * 100)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Split on **bold** pairs. Even-indexed segments stay plain, odd-indexed
// segments become <strong>. Newlines are still honored by the parent
// div's pre-wrap so paragraph breaks keep working. Anthropic's output for
// this prompt uses **bold** only; if it starts emitting italics/lists/
// headings we'll reach for react-markdown.
function renderNarrative(text) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((seg, i) =>
    i % 2 === 1 ? <strong key={i}>{seg}</strong> : <span key={i}>{seg}</span>
  );
}

function NarrativePanel({ scored, basis, period }) {
  // Highest score first so the model sees the dashboard's own ranking.
  const ranked = useMemo(
    () => [...scored].sort((a, b) => b.score.composite - a.score.composite),
    [scored],
  );
  const props = useMemo(() => ranked.map((x) => x.prop), [ranked]);
  const basisLabel =
    basis === "budget" ? "each property's own budget"
    : basis === "trailing12" ? "each property's trailing 12-month average"
    : "portfolio-wide benchmark targets";
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Narrative is cached per period on the server. When the selected month
  // changes, clear local state and fetch whatever is cached for the new
  // period; null = no cache, placeholder shows until user clicks Generate.
  // Cached hits start collapsed (user has likely already read them); fresh
  // generations open expanded further down.
  useEffect(() => {
    let cancelled = false;
    setNarrative(null);
    setLoading(false);
    setCollapsed(false);
    if (!period) return;
    api
      .getNarrative(period)
      .then((data) => {
        if (!cancelled && data?.text) {
          setNarrative(data.text);
          setCollapsed(true);
        }
      })
      .catch(() => {
        // no cache or transient error — leave the placeholder
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const generate = useCallback(async () => {
    setLoading(true);
    // CapEx is lumpy: a month over budget usually means a planned project
    // landed, not an overrun. Describe the YTD position so the narrative
    // judges CapEx against the annual plan rather than a single month.
    const capexLine = (p) => {
      const a = p.capex?.a || 0;
      const b = p.capex?.b || 0;
      const ya = p.capex?.ytd_a;
      const yb = p.capex?.ytd_b;
      let line = `CapEx ${fmt(a)} this month (budget ${fmt(b)})`;
      if (typeof ya === "number" && typeof yb === "number") {
        const diff = Math.abs(ya) - Math.abs(yb);
        const pct = Math.abs(yb) ? Math.round((100 * Math.abs(diff)) / Math.abs(yb)) : null;
        const pos =
          !pct ? "on plan YTD"
          : diff < 0 ? `${pct}% under YTD budget (catching up on planned spend)`
          : `${pct}% over YTD budget`;
        line += `; YTD ${fmt(ya)} vs ${fmt(yb)} budget, ${pos}`;
      }
      return line;
    };
    // Deduction lines: negative = revenue lost, positive = net recovery.
    // Say which, so the model doesn't read a recovery as a loss.
    const ded = (label, f) => {
      const a = f?.a || 0;
      const b = f?.b || 0;
      const desc = a < 0 ? `$${Math.abs(a).toLocaleString()} lost` : a > 0 ? `net recovery of $${a.toLocaleString()} (collections exceeded new losses)` : "$0";
      return `${label} ${desc} (budgeted ${fmt(b)})`;
    };
    // Attach each manager note to the line it was written on, with that
    // line's numbers, and keep any notes that didn't map to a tracked line.
    const NOTE_LINES = [
      ["vacancies", "Vacancies"], ["concessions", "Concessions"], ["bad_debt", "Bad Debt"],
      ["uncollectible", "Uncollectible Non-Rent"], ["loss_to_lease", "Loss to Lease"],
      ["gross_rent", "Rental Income"], ["other_income", "Other Income"], ["total_expense", "Total OpEx"],
      ["noi", "NOI"], ["capex", "CapEx"], ["debt_service", "Debt Service"], ["net_income", "Net Income"],
    ];
    const notesFor = (p) => {
      const out = [];
      const used = new Set();
      for (const [field, label] of NOTE_LINES) {
        const f = p[field];
        if (!f?.n) continue;
        used.add(f.n);
        const sign = f.a > 0 ? "+" : "";
        out.push(`[${label}: actual ${sign}${fmt(f.a)} vs budget ${fmt(f.b)}] "${f.n}"`);
      }
      for (const n of p.notes || []) if (!used.has(n)) out.push(`"${n}"`);
      return out.length ? out.join("; ") : "none";
    };
    const pctVar = (a, b) => (b ? `${a - b >= 0 ? "+" : ""}${Math.round((100 * (a - b)) / Math.abs(b))}%` : "n/a");
    const summary = ranked
      .map(({ prop: p, score }, i) => {
        const m = computeMetrics(p);
        const comps = Object.entries(score.components)
          .map(([k, c]) => {
            const meta = metricMeta[k];
            return `${meta.label} ${meta.fmt(c.actual)} vs target ${meta.fmt(c.target)} → ${Math.round(c.score)}/100`;
          })
          .join("; ");
        const vsBudget = `NOI ${fmt(p.noi.a)} vs budget ${fmt(p.noi.b)} (${pctVar(p.noi.a, p.noi.b)}), Total Income ${fmt(
          p.total_income?.a
        )} vs ${fmt(p.total_income?.b)} (${pctVar(p.total_income?.a || 0, p.total_income?.b || 0)}), OpEx ${fmt(
          p.total_expense?.a
        )} vs ${fmt(p.total_expense?.b)} (${pctVar(p.total_expense?.a || 0, p.total_expense?.b || 0)}), Net Income ${fmt(
          p.net_income.a
        )} vs ${fmt(p.net_income.b)}`;
        return `#${i + 1} ${p.alias} — SCORE ${score.composite}/100 [${comps}]. Budget variance: ${vsBudget}.
${p.alias} (${p.units} units): Occupancy ${m.occ.toFixed(1)}%, NOI ${fmt(p.noi.a)} ($${Math.round(
          m.noiUnit
        )}/unit), Revenue Leakage ${m.leakage.toFixed(1)}% of gross rent, Expense Ratio ${m.expRatio.toFixed(
          0
        )}%, Vacancy $${Math.abs(
          p.vacancies.a
        ).toLocaleString()}, ${ded("Bad Debt", p.bad_debt)}, ${ded("Uncollectible Non-Rent", p.uncollectible)}, ${ded(
          "Concessions",
          p.concessions
        )}, ${capexLine(p)}, Debt Service ${fmt(
          p.debt_service.a
        )} (budget ${fmt(p.debt_service.b)}), Net Income ${fmt(p.net_income.a)}. Manager notes (each tagged with the line it was written on): ${notesFor(p)}`;
      })
      .join("\n\n");

    try {
      const data = await api.narrative({ period, summary, propertyCount: props.length, basis: basisLabel });
      setNarrative(data.text || "No response");
      setCollapsed(false);
    } catch (e) {
      setNarrative("Error: " + e.message);
      setCollapsed(false);
    }
    setLoading(false);
  }, [ranked, props, period, basisLabel]);

  const previewText = narrative
    ? narrative.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\s+/g, " ").slice(0, 120).trim()
    : "";

  const showFull = narrative && !collapsed;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showFull || !narrative ? 10 : 0 }}>
        <button
          type="button"
          onClick={() => narrative && setCollapsed((c) => !c)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: narrative ? "pointer" : "default",
            color: C.text,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "inherit",
          }}
          title={narrative ? (collapsed ? "Expand" : "Collapse") : undefined}
        >
          {narrative && (
            <span
              style={{
                display: "inline-block",
                transition: "transform 0.15s",
                transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
                color: C.sub,
                fontSize: 10,
              }}
            >
              ▼
            </span>
          )}
          Portfolio Narrative
        </button>
        <button
          onClick={generate}
          disabled={loading}
          style={{
            background: loading ? C.borderLight : C.accent,
            color: loading ? C.sub : "#fff",
            border: "none",
            padding: "5px 12px",
            borderRadius: 5,
            fontSize: 11,
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Analyzing…" : narrative ? "Regenerate" : "Generate Summary"}
        </button>
      </div>
      {showFull && (
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
          {renderNarrative(narrative)}
        </div>
      )}
      {narrative && collapsed && (
        <div
          onClick={() => setCollapsed(false)}
          style={{
            fontSize: 12,
            color: C.dim,
            fontStyle: "italic",
            marginTop: 6,
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title="Click to expand"
        >
          {previewText}{previewText.length >= 120 ? "…" : ""}
        </div>
      )}
      {!narrative && (
        <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>
          Generate an AI narrative from this month's operating data and property manager notes.
        </div>
      )}
    </div>
  );
}

function PropertyCard({ p, score, selected, onClick, isOwner, ownerPct }) {
  const m = computeMetrics(p);
  const configured = ownerPct > 0;
  const eNOI = Math.round(p.noi.a * ownerPct);
  const eNI = Math.round(p.net_income.a * ownerPct);
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "50px 1fr",
        gap: 12,
        padding: "14px 16px",
        cursor: "pointer",
        background: selected ? C.accentLight : C.card,
        borderBottom: selected ? "none" : `1px solid ${C.borderLight}`,
        transition: "background 0.15s",
      }}
    >
      <ScoreBadge score={score.composite} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.alias}</span>
            <span style={{ fontSize: 11, color: C.dim, marginLeft: 8 }}>
              {p.units} units
            </span>
            {isOwner && configured && (
              <span style={{ fontSize: 11, color: C.accent, marginLeft: 6, fontWeight: 600 }}>
                {(ownerPct * 100).toFixed(1)}%
              </span>
            )}
            {isOwner && !configured && (
              <span
                style={{
                  fontSize: 10,
                  color: C.amber,
                  marginLeft: 6,
                  fontWeight: 600,
                  padding: "1px 5px",
                  background: C.amberBg,
                  borderRadius: 3,
                }}
              >
                % not set
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 14, flexShrink: 0 }}>
            {isOwner ? (
              configured ? (
                <>
                  <Stat label="My NOI" val={fmt(eNOI)} color={C.accent} bold />
                  <Stat label="My NI" val={fmt(eNI)} color={eNI < 0 ? C.red : C.accent} />
                  <Stat
                    label="Property NOI"
                    val={fmt(p.noi.a)}
                    color={C.sub}
                    leftBorder
                  />
                </>
              ) : (
                <>
                  <Stat label="NOI" val={fmt(p.noi.a)} color={C.text} />
                  <Stat label="Occ" val={`${m.occ.toFixed(1)}%`} color={m.occ < 89 ? C.amber : C.green} />
                </>
              )
            ) : (
              <>
                <Stat label="NOI" val={fmt(p.noi.a)} color={C.text} />
                <Stat label="NOI/Unit" val={`$${Math.round(m.noiUnit)}`} color={C.text} />
                <Stat label="Occ" val={`${m.occ.toFixed(1)}%`} color={m.occ < 89 ? C.amber : C.green} />
              </>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
          {Object.entries(score.components).map(([key, c]) => {
            const label = {
              occupancy: "Occupancy",
              noiUnit: "NOI/Unit",
              leakage: "Leakage",
              expRatio: "Expenses",
            }[key];
            const bg = scoreColor(c.score);
            const num = Math.round(c.score / 10);
            return (
              <div
                key={key}
                style={{
                  padding: "3px 8px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontFamily: "'DM Mono',monospace",
                  background: `${bg}15`,
                  color: bg,
                  border: `1px solid ${bg}25`,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span style={{ fontWeight: 400 }}>{label}</span>
                <span style={{ fontWeight: 700 }}>{num}/10</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, val, color, bold, leftBorder }) {
  return (
    <div
      style={{
        textAlign: "right",
        borderLeft: leftBorder ? `1px solid ${C.borderLight}` : "none",
        paddingLeft: leftBorder ? 12 : 0,
      }}
    >
      <div style={{ fontSize: 10, color: C.dim }}>{label}</div>
      <div
        style={{
          fontSize: 12,
          fontFamily: "'DM Mono',monospace",
          color: color,
          fontWeight: bold ? 600 : 400,
        }}
      >
        {val}
      </div>
    </div>
  );
}

function DetailPanel({ p, score, weights, ownerPct, ttm }) {
  const [detailBasis, setDetailBasis] = useState("budget");
  const m = computeMetrics(p);
  const configured = ownerPct > 0;
  const hasTTM = Boolean(ttm);

  const getCompare = (field) => (detailBasis === "ttm" && hasTTM ? ttm[field] : p[field].b);
  const basisLabel = detailBasis === "ttm" && hasTTM ? "TTM Avg" : "Budget";

  const lines = [
    { l: "Gross Possible Rent", field: "gross_rent", a: p.gross_rent.a },
    { l: "Loss / Gain to Lease", field: "loss_to_lease", a: p.loss_to_lease.a, leak: true },
    { l: "Vacancies", field: "vacancies", a: p.vacancies.a, leak: true },
    { l: "Concessions", field: "concessions", a: p.concessions.a, leak: true },
    { l: "Bad Debt", field: "bad_debt", a: p.bad_debt.a, leak: true },
    { l: "Uncollectible", field: "uncollectible", a: p.uncollectible.a, leak: true },
    { l: "Net Rental Income", field: "net_rental", a: p.net_rental.a, bold: true },
    { l: "Other Income", field: "other_income", a: p.other_income.a },
    { l: "Total Income", field: "total_income", a: p.total_income.a, bold: true },
    { l: "Total OpEx", field: "total_expense", a: -p.total_expense.a, neg: true },
    { l: "NOI", field: "noi", a: p.noi.a, bold: true, highlight: true },
    { l: "CapEx", field: "capex", a: -p.capex.a, neg: true },
    { l: "Debt Service", field: "debt_service", a: -p.debt_service.a, neg: true },
    { l: "Net Income", field: "net_income", a: p.net_income.a, bold: true },
  ];

  const vPct = (a, b) => (b ? ((a - b) / Math.abs(b)) * 100 : 0);
  const vClr = (pct) => (Math.abs(pct) < 3 ? C.dim : pct > 0 ? C.green : C.red);

  return (
    <div style={{ background: C.bg, borderLeft: `3px solid ${C.accent}`, padding: "20px 20px 20px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 400, color: C.text, fontFamily: "'Instrument Serif',serif" }}>
            {p.name}
          </div>
          <div style={{ fontSize: 11, color: C.sub }}>
            {p.units} units
          </div>
          {configured && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 12px",
                background: C.accentLight,
                borderRadius: 5,
                border: `1px solid ${C.accent}20`,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, marginBottom: 6 }}>
                Owner's Share ({(ownerPct * 100).toFixed(1)}%)
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {[
                  { l: "NOI", v: Math.round(p.noi.a * ownerPct), expense: false },
                  { l: "Net Income", v: Math.round(p.net_income.a * ownerPct), expense: false },
                  { l: "CapEx", v: Math.round(p.capex.a * ownerPct), expense: true },
                  { l: "Debt Exposure", v: Math.round(p.debt_service.a * ownerPct), expense: true },
                ].map((x, i) => {
                  // Expenses show as positive magnitude in neutral text color
                  // (no budget on this strip to color by valence). Income
                  // items stay signed so a negative Net Income reads red.
                  const display = x.expense ? Math.abs(x.v) : x.v;
                  const color = x.expense ? C.text : x.v < 0 ? C.red : C.accent;
                  return (
                    <div key={i}>
                      <span style={{ fontSize: 11, color: C.sub }}>{x.l}: </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontFamily: "'DM Mono',monospace",
                          fontWeight: 600,
                          color,
                        }}
                      >
                        {fmt(display)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!configured && (
            <div style={{ marginTop: 8, padding: "8px 12px", background: C.amberBg, borderRadius: 5 }}>
              <span style={{ fontSize: 11, color: C.amber }}>
                Ownership % not configured — set in ⚙ Settings to see owner's share.
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { l: "Occupancy", v: `${m.occ.toFixed(1)}%`, c: m.occ < 89 ? C.amber : C.green },
            { l: "NOI/Unit", v: `$${Math.round(m.noiUnit)}`, c: C.text },
            { l: "Leakage", v: `${m.leakage.toFixed(1)}%`, c: m.leakage > 6 ? C.red : C.text },
            { l: "Exp Ratio", v: `${m.expRatio.toFixed(0)}%`, c: m.expRatio > 55 ? C.amber : C.text },
          ].map((x, i) => (
            <div
              key={i}
              style={{ textAlign: "center", padding: "6px 10px", background: C.bg, borderRadius: 5, minWidth: 60 }}
            >
              <div style={{ fontSize: 10, color: C.dim }}>{x.l}</div>
              <div style={{ fontSize: 14, fontWeight: 600, fontFamily: "'DM Mono',monospace", color: x.c }}>
                {x.v}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Score Breakdown</div>
        <ScoreBreakdown result={score} weights={weights} />
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Income Statement</div>
          <div style={{ display: "flex", gap: 3 }}>
            {[
              { id: "budget", l: "vs Budget" },
              { id: "ttm", l: "vs TTM", disabled: !hasTTM },
            ].map((b) => (
              <button
                key={b.id}
                onClick={() => !b.disabled && setDetailBasis(b.id)}
                style={{
                  background: detailBasis === b.id ? C.accent : "transparent",
                  color: detailBasis === b.id ? "#fff" : C.sub,
                  border: `1px solid ${detailBasis === b.id ? C.accent : C.border}`,
                  padding: "3px 10px",
                  borderRadius: 4,
                  fontSize: 11,
                  cursor: b.disabled ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  opacity: b.disabled ? 0.5 : 1,
                }}
              >
                {b.l}
                {b.disabled ? " (no data)" : ""}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 700 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 90px 90px 60px 20px 90px 90px 60px",
                padding: "6px 0",
                borderBottom: `2px solid ${C.border}`,
                fontSize: 10,
                color: C.dim,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                gap: 4,
              }}
            >
              <span></span>
              <span style={{ textAlign: "right" }}>Actual</span>
              <span style={{ textAlign: "right" }}>{basisLabel}</span>
              <span style={{ textAlign: "right" }}>Var%</span>
              <span></span>
              <span style={{ textAlign: "right" }}>YTD Actual</span>
              <span style={{ textAlign: "right" }}>YTD Budget</span>
              <span style={{ textAlign: "right" }}>Var%</span>
            </div>

            {lines.map((r, i) => {
              const comp = getCompare(r.field);
              const compVal = r.neg ? -comp : comp;
              const pct = vPct(r.a, compVal);
              // Parser stores ytd_a / ytd_b per-field; fall back to the
              // monthly value for legacy snapshots missing those keys.
              const ytdRawA = p[r.field].ytd_a ?? p[r.field].a;
              const ytdRawB = p[r.field].ytd_b ?? p[r.field].b;
              const ytdA = r.neg ? -ytdRawA : ytdRawA;
              const ytdB = r.neg ? -ytdRawB : ytdRawB;
              const ytdPct = vPct(ytdA, ytdB);

              // Expense rows (leak deductions + below-NOI spend) are shown as
              // positive magnitudes and colored by valence vs budget (under =
              // green, over = red). Income rows keep signed values colored by
              // raw sign so a negative Net Income still reads red.
              const isExpense = r.neg;
              const actualVal = isExpense ? Math.abs(r.a) : r.a;
              const actualColor = (isExpense || r.leak) ? vClr(pct) : r.a < 0 ? C.red : C.text;
              const budgetVal = isExpense ? Math.abs(compVal) : compVal;
              const ytdActualVal = isExpense ? Math.abs(ytdA) : ytdA;
              const ytdActualColor = (isExpense || r.leak) ? vClr(ytdPct) : ytdA < 0 ? C.red : C.text;
              const ytdBudgetVal = isExpense ? Math.abs(ytdB) : ytdB;

              return (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 90px 90px 60px 20px 90px 90px 60px",
                    padding: "4px 0",
                    gap: 4,
                    alignItems: "center",
                    borderBottom: `1px solid ${C.borderLight}`,
                    fontWeight: r.bold ? 600 : 400,
                    fontSize: 11,
                    background: r.highlight ? C.accentLight : "transparent",
                  }}
                >
                  <span style={{ color: r.leak ? C.red : C.text }}>
                    {r.l}
                    {r.leak ? " ⚡" : ""}
                  </span>
                  <span
                    style={{
                      textAlign: "right",
                      fontFamily: "'DM Mono',monospace",
                      color: actualColor,
                    }}
                  >
                    {fmt(actualVal)}
                  </span>
                  <span style={{ textAlign: "right", fontFamily: "'DM Mono',monospace", color: C.dim }}>
                    {fmt(budgetVal)}
                  </span>
                  <span
                    style={{
                      textAlign: "right",
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 11,
                      color: vClr(pct),
                    }}
                  >
                    {pct > 0 ? "+" : ""}
                    {pct.toFixed(0)}%
                  </span>
                  <span style={{ textAlign: "center", color: C.borderLight, fontSize: 11 }}>│</span>
                  <span
                    style={{
                      textAlign: "right",
                      fontFamily: "'DM Mono',monospace",
                      color: ytdActualColor,
                    }}
                  >
                    {fmt(ytdActualVal)}
                  </span>
                  <span style={{ textAlign: "right", fontFamily: "'DM Mono',monospace", color: C.dim }}>
                    {fmt(ytdBudgetVal)}
                  </span>
                  <span
                    style={{
                      textAlign: "right",
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 11,
                      color: vClr(ytdPct),
                    }}
                  >
                    {ytdPct > 0 ? "+" : ""}
                    {ytdPct.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {p.notes && p.notes.length > 0 && (
        <div style={{ marginTop: 16, padding: "12px 16px", background: "#FFF9F0", borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.amber, marginBottom: 6 }}>Manager Notes</div>
          {p.notes.map((n, i) => (
            <div key={i} style={{ fontSize: 12, color: C.text, padding: "3px 0" }}>
              • {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Dashboard({ demo = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(fallbackPeriod);
  const [periods, setPeriods] = useState([fallbackPeriod]);
  const [properties, setProperties] = useState(fallbackProperties);
  const [allSnapshots, setAllSnapshots] = useState([]);
  const [ttmByCode, setTtmByCode] = useState({});

  const [selected, setSelected] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTab, setManageTab] = useState("scoring");
  const [basis, setBasis] = useState("benchmark");
  const [benchmarks, setBenchmarks] = useState(defaultBenchmarks);
  const [weights, setWeights] = useState(defaultWeights);
  const [sortOrder, setSortOrder] = useState("score-desc");
  const [view, setView] = useState("ops");
  const [ownerPcts, setOwnerPcts] = useState(() => {
    const init = {};
    fallbackProperties.forEach((p) => {
      init[p.code] = p.edwardPct || 0;
    });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const loadForPeriod = useCallback(async (p) => {
    try {
      const snaps = await api.listSnapshots(p);
      if (snaps && snaps.length) {
        setProperties(snaps);
      } else {
        setProperties([]);
      }
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settings, periodList] = await Promise.all([api.getSettings(), api.listPeriods()]);
        if (cancelled) return;
        if (settings) {
          if (settings.benchmarks) setBenchmarks({ ...defaultBenchmarks, ...pickScoredMetrics(settings.benchmarks) });
          if (settings.weights) setWeights({ ...defaultWeights, ...pickScoredMetrics(settings.weights) });
          if (settings.basis) setBasis(settings.basis);
          if (settings.ownership) setOwnerPcts((prev) => ({ ...prev, ...settings.ownership }));
        }
        const list = periodList && periodList.length ? periodList : [fallbackPeriod];
        setPeriods(list);
        const latest = list[0];
        setPeriod(latest);
        await loadForPeriod(latest);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadForPeriod]);

  // Fetch every snapshot once (and again on new uploads). Shared by the TTM
  // calc, the 12-month trend chart, and the CapEx progress bar.
  const refreshAllSnapshots = useCallback(async () => {
    try {
      const all = await api.listSnapshots();
      setAllSnapshots(all || []);
    } catch {
      // ignore — history is optional
    }
  }, []);

  useEffect(() => {
    refreshAllSnapshots();
  }, [refreshAllSnapshots]);

  useEffect(() => {
    if (!properties || properties.length === 0) {
      setTtmByCode({});
      return;
    }
    if (!allSnapshots || allSnapshots.length === 0) {
      setTtmByCode({});
      return;
    }
    // Compute TTM per property from the 12 most recent periods ending at `period`.
    const byCode = {};
    const cutoffIdx = periods.indexOf(period);
    const window = periods.slice(cutoffIdx, cutoffIdx + 12);
    for (const code of properties.map((p) => p.code)) {
      const rows = allSnapshots
        .filter((s) => s.code === code && window.includes(s.period))
        .slice(0, 12);
      if (rows.length >= 3) {
        byCode[code] = aggregateTTM(rows);
      }
    }
    setTtmByCode(byCode);
  }, [properties, period, periods, allSnapshots]);

  const getPct = (p) => ownerPcts[p.code] || 0;
  const isConfigured = (p) => getPct(p) > 0;
  const isOwner = view === "owner";
  const ttmAvailable = Object.keys(ttmByCode).length > 0;

  const scoredRaw = useMemo(
    () =>
      properties.map((p) => {
        const ttm = ttmByCode[p.code];
        const ttmMetrics = ttm
          ? {
              occ: ttm.gross_rent
                ? (1 - Math.abs(ttm.deductions) / ttm.gross_rent) * 100
                : 0,
              noiUnit: p.units ? ttm.noi / p.units : 0,
              leakage: ttm.gross_rent
                ? (netLeakage(ttm.bad_debt, ttm.uncollectible, ttm.concessions) / ttm.gross_rent) * 100
                : 0,
              expRatio: ttm.total_income ? (ttm.total_expense / ttm.total_income) * 100 : 0,
            }
          : null;
        return { prop: p, score: computeScore(p, basis, benchmarks, weights, ttmMetrics) };
      }),
    [properties, basis, benchmarks, weights, ttmByCode]
  );

  const configuredProps = scoredRaw.filter((x) => isConfigured(x.prop));

  const scored = [...scoredRaw].sort((a, b) => {
    if (sortOrder === "score-asc") return a.score.composite - b.score.composite;
    if (sortOrder === "score-desc") return b.score.composite - a.score.composite;
    if (sortOrder === "alpha") return a.prop.alias.localeCompare(b.prop.alias);
    if (sortOrder === "owner-noi")
      return b.prop.noi.a * getPct(b.prop) - a.prop.noi.a * getPct(a.prop);
    if (sortOrder === "owner-ni")
      return b.prop.net_income.a * getPct(b.prop) - a.prop.net_income.a * getPct(a.prop);
    return 0;
  });

  const allProps = scoredRaw.map((x) => x.prop);
  const unconfiguredCount = allProps.filter((p) => !isConfigured(p)).length;

  const totals = {
    noi: allProps.reduce((s, p) => s + (isOwner ? Math.round(p.noi.a * getPct(p)) : p.noi.a), 0),
    ni: allProps.reduce((s, p) => s + (isOwner ? Math.round(p.net_income.a * getPct(p)) : p.net_income.a), 0),
    capex: allProps.reduce((s, p) => s + (isOwner ? Math.round(p.capex.a * getPct(p)) : p.capex.a), 0),
    debt: allProps.reduce(
      (s, p) => s + (isOwner ? Math.round(p.debt_service.a * getPct(p)) : p.debt_service.a),
      0
    ),
    units: allProps.reduce((s, p) => s + p.units, 0),
    income: allProps.reduce(
      (s, p) => s + (isOwner ? Math.round(p.total_income.a * getPct(p)) : p.total_income.a),
      0
    ),
  };

  const avgScore =
    isOwner && configuredProps.length > 0
      ? Math.round(
          configuredProps.reduce((s, x) => s + x.score.composite * getPct(x.prop), 0) /
            configuredProps.reduce((s, x) => s + getPct(x.prop), 0)
        )
      : scoredRaw.length
      ? Math.round(scoredRaw.reduce((s, x) => s + x.score.composite, 0) / scoredRaw.length)
      : 0;

  const avgOcc = allProps.length
    ? allProps.reduce((s, p) => s + (1 - Math.abs(p.deductions.a) / p.gross_rent.a) * 100, 0) / allProps.length
    : 0;

  const handleSaveSettings = useCallback(async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await api.saveSettings({ benchmarks, weights, basis, ownership: ownerPcts });
      setSaveStatus("Saved");
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      setSaveStatus("Error: " + e.message);
    }
    setSaving(false);
  }, [benchmarks, weights, basis, ownerPcts]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    setSelected(null);
    setLoading(true);
    loadForPeriod(p).finally(() => setLoading(false));
  };

  const openManage = (tab = "scoring") => {
    setManageTab(tab);
    setManageOpen(true);
  };

  const handleUploaded = async (res) => {
    const list = (await api.listPeriods()) || periods;
    setPeriods(list);
    // Auto-select the period we just uploaded so the user can immediately
    // verify the data landed without a trip through the period dropdown.
    const nextPeriod = res?.primary_period || res?.uploaded?.[0]?.period || period;
    if (nextPeriod && list.includes(nextPeriod)) {
      setPeriod(nextPeriod);
      setSelected(null);
      await loadForPeriod(nextPeriod);
    } else {
      await loadForPeriod(period);
    }
    // Refresh the full snapshot cache so trend chart + CapEx bar pick up
    // the new upload without a page reload.
    await refreshAllSnapshots();
  };

  // After editing properties in the Manage modal, persist ownership % to
  // settings so other clients see the new values on next load.
  const handlePropertiesSaved = useCallback(async (nextOwnerPcts) => {
    try {
      await api.saveSettings({ benchmarks, weights, basis, ownership: nextOwnerPcts });
      // Re-fetch snapshots so any alias/units/owner edits — and archive /
      // restore — flow into cards, KPI totals, trend chart, and CapEx bar.
      await loadForPeriod(period);
      await refreshAllSnapshots();
    } catch (e) {
      setError(e.message);
    }
  }, [benchmarks, weights, basis, period, loadForPeriod, refreshAllSnapshots]);

  const periodLabel = formatPeriod(period);

  return (
    <div
      style={{
        background: C.bg,
        minHeight: "100vh",
        fontFamily: "'Inter',sans-serif",
        padding: "0 20px 40px",
        maxWidth: 1180,
        margin: "0 auto",
      }}
    >
      <div style={{ padding: "20px 0 14px", borderBottom: `1px solid ${C.border}`, marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 13,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: C.accent,
                fontFamily: "'DM Mono',monospace",
              }}
            >
              {periodLabel}
            </div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 400,
                margin: "2px 0 0",
                fontFamily: "'Instrument Serif',serif",
                color: C.text,
              }}
            >
              {isOwner ? (demo ? "Owner Share" : "RRC Share") : "Property Totals"}
            </h1>
            <div style={{ fontSize: 11, color: C.sub }}>
              {allProps.length} properties · {totals.units.toLocaleString()} units ·{" "}
              {isOwner ? "Weighted by ownership" : "Equal weight"} · Scoring:{" "}
              {basis === "benchmark" ? "Benchmarks" : basis === "budget" ? "Budget" : "TTM"}
              {isOwner && unconfiguredCount > 0 && (
                <span style={{ color: C.amber, marginLeft: 6 }}>· {unconfiguredCount} without ownership %</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {periods.length > 0 && (
              <select
                value={period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                style={{
                  padding: "6px 10px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 5,
                  fontSize: 11,
                  fontFamily: "'DM Mono',monospace",
                  background: C.card,
                  color: C.text,
                }}
              >
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {formatPeriod(p)}
                  </option>
                ))}
              </select>
            )}
            <div
              style={{
                display: "flex",
                gap: 3,
                background: C.card,
                borderRadius: 6,
                padding: 3,
                border: `1px solid ${C.border}`,
              }}
            >
              {[
                { id: "owner", l: demo ? "Owner" : "RRC" },
                { id: "ops", l: "Total" },
              ].map((v) => (
                <button
                  key={v.id}
                  onClick={() => {
                    setView(v.id);
                    setSelected(null);
                    setSortOrder("score-desc");
                  }}
                  style={{
                    background: view === v.id ? C.accent : "transparent",
                    color: view === v.id ? "#fff" : C.sub,
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {v.l}
                </button>
              ))}
            </div>
            {!demo && (
              <button
                type="button"
                onClick={() => openManage("uploads")}
                style={{
                  background: C.card,
                  color: C.sub,
                  border: `1px solid ${C.border}`,
                  minWidth: 40,
                  minHeight: 40,
                  padding: 0,
                  borderRadius: 5,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Upload ResMan workbook"
              >
                <IconUpload size={fz(20)} />
              </button>
            )}
            {!demo && (
              <button
                type="button"
                onClick={() => openManage("scoring")}
                style={{
                  background: C.card,
                  color: C.sub,
                  border: `1px solid ${C.border}`,
                  minWidth: 40,
                  minHeight: 40,
                  padding: 0,
                  borderRadius: 5,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Manage properties, scoring, uploads"
              >
                <IconGear size={fz(20)} />
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            background: C.redBg,
            color: C.red,
            border: `1px solid ${C.red}40`,
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <ManageModal
        open={manageOpen}
        tab={manageTab}
        onChangeTab={setManageTab}
        onClose={() => setManageOpen(false)}
        properties={allProps.length ? allProps : fallbackProperties}
        ttmAvailable={ttmAvailable}
        basis={basis}
        setBasis={setBasis}
        benchmarks={benchmarks}
        setBenchmarks={setBenchmarks}
        weights={weights}
        setWeights={setWeights}
        ownerPcts={ownerPcts}
        setOwnerPcts={setOwnerPcts}
        onSaveSettings={handleSaveSettings}
        onUploaded={handleUploaded}
        onPropertiesSaved={handlePropertiesSaved}
        saving={saving}
        saveStatus={saveStatus}
      />

      {loading && allProps.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: C.sub, fontSize: 13 }}>Loading…</div>
      ) : allProps.length === 0 ? (
        <EmptyState onOpenUpload={() => openManage("uploads")} />
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                background: scoreBg(avgScore),
                border: `2px solid ${scoreColor(avgScore)}`,
                borderRadius: 6,
                padding: "10px 12px",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: C.sub,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {isOwner ? "Portfolio Score" : "Avg Score"}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: "'DM Mono',monospace",
                  color: scoreColor(avgScore),
                }}
              >
                {avgScore}
              </div>
            </div>
            {(isOwner
              ? [
                  { l: "My NOI", v: fmt(totals.noi), c: C.accent },
                  { l: "My Net Income", v: fmt(totals.ni), c: totals.ni < 0 ? C.red : C.green },
                  { l: "My CapEx", v: fmt(totals.capex) },
                  { l: "My Debt Svc", v: fmt(totals.debt) },
                  { l: "Avg Occ", v: `${avgOcc.toFixed(1)}%`, c: avgOcc < 90 ? C.amber : C.green },
                ]
              : [
                  { l: "Total NOI", v: fmt(totals.noi) },
                  { l: "Net Income", v: fmt(totals.ni), c: totals.ni < 0 ? C.red : C.green },
                  { l: "Total CapEx", v: fmt(totals.capex) },
                  { l: "NOI / Unit", v: `$${Math.round(totals.noi / Math.max(totals.units, 1))}` },
                  { l: "Avg Occ", v: `${avgOcc.toFixed(1)}%`, c: avgOcc < 90 ? C.amber : C.green },
                ]
            ).map((m, i) => (
              <div
                key={i}
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: C.sub,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {m.l}
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    fontFamily: "'DM Mono',monospace",
                    color: m.c || C.text,
                    marginTop: 1,
                  }}
                >
                  {m.v}
                </div>
              </div>
            ))}
          </div>

          <NarrativePanel scored={scoredRaw} basis={basis} period={period} />

          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "12px 12px 4px",
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              {isOwner ? "Owner's NOI by Property" : "NOI by Property"}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={scored.map(({ prop: p, score: s }) => ({
                  name: p.alias,
                  noi: isOwner ? Math.round(p.noi.a * getPct(p)) : p.noi.a,
                  score: s.composite,
                  pct: getPct(p),
                }))}
                margin={{ top: 5, right: 5, left: 5, bottom: 40 }}
              >
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.sub }} interval={0} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: C.dim }} tickFormatter={fmt} />
                <Tooltip
                  formatter={(v, name, entry) => {
                    if (isOwner) return [fmt(v) + ` (${(entry.payload.pct * 100).toFixed(1)}%)`, "Owner's NOI"];
                    return [fmt(v), "NOI"];
                  }}
                />
                <Bar dataKey="noi" radius={[4, 4, 0, 0]} name="NOI">
                  {scored.map(({ score: s }, i) => (
                    <Cell key={i} fill={scoreColor(s.composite)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <div
              style={{
                padding: "10px 16px",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                {isOwner ? `Properties (${scored.length})` : `All Properties (${scored.length})`}
              </span>
              <div style={{ display: "flex", gap: 3 }}>
                {(isOwner
                  ? [
                      { id: "owner-noi", l: "My NOI" },
                      { id: "owner-ni", l: "My NI" },
                      { id: "score-desc", l: "Score ↓" },
                      { id: "score-asc", l: "Score ↑" },
                      { id: "alpha", l: "A–Z" },
                    ]
                  : [
                      { id: "score-desc", l: "Score ↓" },
                      { id: "score-asc", l: "Score ↑" },
                      { id: "alpha", l: "A–Z" },
                    ]
                ).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSortOrder(s.id)}
                    style={{
                      background: sortOrder === s.id ? C.accent : "transparent",
                      color: sortOrder === s.id ? "#fff" : C.sub,
                      border: `1px solid ${sortOrder === s.id ? C.accent : C.border}`,
                      padding: "3px 10px",
                      borderRadius: 4,
                      fontSize: 11,
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            {scored.map(({ prop: p, score: s }) => (
              <div key={p.code}>
                <PropertyCard
                  p={p}
                  score={s}
                  selected={selected === p.code}
                  onClick={() => setSelected(selected === p.code ? null : p.code)}
                  isOwner={isOwner}
                  ownerPct={getPct(p)}
                />
                {selected === p.code && (
                  <div style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                    <DetailPanel p={p} score={s} weights={weights} ownerPct={getPct(p)} ttm={ttmByCode[p.code]} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <TrendChart
            allSnapshots={allSnapshots}
            properties={properties}
            periods={periods}
            period={period}
            isOwner={isOwner}
            getPct={getPct}
            basis={basis}
            benchmarks={benchmarks}
            weights={weights}
            demo={demo}
          />

          <CapExProgress
            properties={properties}
            isOwner={isOwner}
            getPct={getPct}
            period={period}
            demo={demo}
          />
        </>
      )}

      <div
        style={{
          marginTop: 20,
          padding: "10px 0",
          borderTop: `1px solid ${C.border}`,
          fontSize: 11,
          color: C.dim,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>Source: ResMan · {periodLabel} · Accrual</span>
      </div>
    </div>
  );
}

function EmptyState({ onOpenUpload }) {
  return (
    <div
      style={{
        padding: 60,
        textAlign: "center",
        background: C.card,
        border: `1px dashed ${C.border}`,
        borderRadius: 8,
      }}
    >
      <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 22, color: C.text, marginBottom: 4 }}>
        No snapshots yet
      </div>
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 14 }}>
        Upload a ResMan Budget Variance export to populate this period.
      </div>
      <button
        onClick={onOpenUpload}
        style={{
          background: C.accent,
          color: "#fff",
          border: "none",
          borderRadius: 5,
          padding: "8px 16px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Upload export
      </button>
    </div>
  );
}

function formatPeriod(p) {
  if (!p) return "";
  const [y, m] = p.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

export default function App() {
  return (
    <LoginGate>
      {({ demo }) => <Dashboard demo={demo} />}
    </LoginGate>
  );
}
